import React, { useEffect, useState } from 'react';
import {
  CheckCircle,
  ShieldCheck,
  User,
  Calendar,
  FileText,
  Activity,
  Link,
  XCircle,
  Clock,
  Shield,
  Search,
  ArrowRight,
  Loader2
} from 'lucide-react';
import { CertificateVerificationResult, SystemConfig } from '@/types';
import { api } from '@/services/api';
import { formatCertificateDate } from '@/lib/certificateRenderer';
import { formatUkDateTime } from '@/lib/dates';
import { normalizeErrorMessage } from '@/lib/errorUtils';

interface VerificationPageProps {
  systemConfig?: SystemConfig;
}

export const VerificationPage: React.FC<VerificationPageProps> = ({ systemConfig }) => {
  // Parse URL Parameters
  const params = new URLSearchParams(window.location.search);
  const certId = params.get('certId') || params.get('ref'); // Backward compatibility

  const config = systemConfig?.verification || {
    showName: true,
    showDate: true,
    showRef: true,
    showOutcome: true,
    showExpiry: true,
    requireNameConfirmation: true, // Default
    validMessage: 'This certificate is genuine and valid.',
    showUrl: false,
    urlOverride: ''
  };

  const logoUrl =
    systemConfig?.general.logoUrl || 'https://placehold.co/150x50/009FE3/ffffff?text=K2+OH';

  // State for Name Verification
  const [enteredName, setEnteredName] = useState('');
  const [error, setError] = useState('');
  const [verificationResult, setVerificationResult] =
    useState<CertificateVerificationResult | null>(null);
  const [isLoadingCertificate, setIsLoadingCertificate] = useState(Boolean(certId));
  const [certificateError, setCertificateError] = useState('');

  // Determine display URL
  const displayUrl =
    config.urlOverride || systemConfig?.general.appUrl || window.location.href.split('?')[0];

  useEffect(() => {
    let isActive = true;
    setEnteredName('');
    setError('');

    if (!certId) {
      setVerificationResult(null);
      setIsLoadingCertificate(false);
      setCertificateError('');
      return () => {
        isActive = false;
      };
    }

    setIsLoadingCertificate(true);
    setCertificateError('');
    api
      .verifyCertificatePublic(certId)
      .then((result) => {
        if (!isActive) return;
        setVerificationResult(result);
      })
      .catch((err: unknown) => {
        if (!isActive) return;
        setVerificationResult(null);
        setCertificateError(normalizeErrorMessage(err, 'Certificate verification failed.'));
      })
      .finally(() => {
        if (!isActive) return;
        setIsLoadingCertificate(false);
      });

    return () => {
      isActive = false;
    };
  }, [certId]);

  const certificate = verificationResult?.certificate || null;
  const requiresNameConfirmation = Boolean(verificationResult?.requiresNameConfirmation);
  const isNameConfirmed = Boolean(verificationResult?.nameConfirmed);
  const lifecycleStatus = verificationResult?.lifecycleStatus || 'valid';
  const certificateName = String(certificate?.customerName || '').trim();
  const issuedAt = String(certificate?.issuedAt || '').trim();
  const validUntil = String(certificate?.validUntil || '').trim();
  const certificateOutcome = String(certificate?.outcome || '').trim();
  const resolvedCertId = String(certificate?.id || certId || '').trim();
  const revokedAt = String(certificate?.revokedAt || '').trim();
  const isRevoked = lifecycleStatus === 'revoked';

  const handleVerifyName = async (e: React.FormEvent) => {
    e.preventDefault();
    const candidateName = enteredName.trim();
    if (!candidateName || !certId) {
      setError('Please enter the patient name exactly as on the certificate.');
      return;
    }

    setIsLoadingCertificate(true);
    setError('');
    try {
      const result = await api.verifyCertificatePublic(certId, candidateName);
      setVerificationResult(result);
      if (!result.nameConfirmed) {
        setError('The name entered does not match the certificate record. Please try again.');
      }
    } catch (err: unknown) {
      setError(normalizeErrorMessage(err, 'Unable to verify the name right now.'));
    } finally {
      setIsLoadingCertificate(false);
    }
  };

  // Server is the source of truth for expiry. Local check stays as a defense-in-depth
  // fallback only — used to avoid showing "valid" if the local clock has already moved
  // past the expiry while the server reply is in flight.
  let isLapsed = lifecycleStatus === 'expired';
  if (!isLapsed && !isRevoked && validUntil) {
    const expiryDate = new Date(validUntil);
    if (!Number.isNaN(expiryDate.getTime())) {
      expiryDate.setHours(23, 59, 59, 999);
      if (expiryDate.getTime() < Date.now()) {
        isLapsed = true;
      }
    }
  }

  // --- Render Case 1: Missing ID ---
  if (!certId) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-sm w-full text-center border border-slate-200">
          <ShieldCheck className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-slate-900 mb-2">Verification Portal</h1>
          <p className="text-slate-500 text-sm">
            Please scan a valid QR code from a medical certificate to verify its authenticity.
          </p>
        </div>
      </div>
    );
  }

  // --- Render Case 2: Loading ---
  if (isLoadingCertificate) {
    return (
      <div className="min-h-screen bg-slate-100 flex flex-col items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8 border border-slate-200 text-center">
          <Loader2 className="w-10 h-10 animate-spin text-teal-600 mx-auto mb-4" />
          <h2 className="text-lg font-bold text-slate-800 mb-2">Validating Certificate</h2>
          <p className="text-sm text-slate-500">Please wait while we verify this certificate.</p>
        </div>
      </div>
    );
  }

  // --- Render Case 3: Invalid/Unavailable ---
  if (certificateError || !certificate) {
    return (
      <div className="min-h-screen bg-slate-100 flex flex-col items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8 border border-slate-200 text-center">
          <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-lg font-bold text-slate-900 mb-2">Certificate Not Valid</h2>
          <p className="text-sm text-slate-600">
            {certificateError || 'No certificate record was found for this reference.'}
          </p>
        </div>
      </div>
    );
  }

  // --- Render Case 4: Security Check Required ---
  if (requiresNameConfirmation && !isNameConfirmed) {
    return (
      <div className="min-h-screen bg-slate-100 flex flex-col items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden animate-fade-in border border-slate-200">
          <div className="bg-slate-50 p-6 border-b border-slate-100 flex flex-col items-center">
            <img src={logoUrl} alt="Logo" className="h-10 mb-4 object-contain" />
            <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
              <Shield className="w-5 h-5 text-teal-600" /> Security Check
            </h2>
          </div>

          <div className="p-8">
            <p className="text-slate-600 text-sm mb-6 text-center">
              To view this certificate's details, please confirm the name of the individual
              associated with it.
            </p>

            <form onSubmit={handleVerifyName} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-2">
                  Full Name
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-3 text-slate-400 w-5 h-5" />
                  <input
                    type="text"
                    value={enteredName}
                    onChange={(e) => setEnteredName(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border rounded-xl outline-none focus:ring-2 focus:ring-teal-500"
                    placeholder="Enter full name"
                    required
                  />
                </div>
              </div>

              {error && (
                <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg text-sm flex items-start gap-2">
                  <XCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  {error}
                </div>
              )}

              <button
                type="submit"
                className="w-full bg-slate-900 text-white py-3 rounded-xl font-bold hover:bg-slate-800 transition-colors flex items-center justify-center gap-2"
              >
                Verify Identity <ArrowRight className="w-4 h-4" />
              </button>
            </form>
          </div>
          <div className="bg-slate-50 p-4 text-center border-t border-slate-100">
            <p className="text-xs text-slate-400">Secure Verification System</p>
          </div>
        </div>
      </div>
    );
  }

  // --- Render Case 5: Display Certificate ---
  return (
    <div className="min-h-screen bg-slate-100 flex flex-col items-center py-10 px-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden animate-fade-in border border-slate-200">
        {/* Header */}
        <div className="bg-white border-b border-slate-100 p-6 flex flex-col items-center">
          <img src={logoUrl} alt="Logo" className="h-10 mb-4 object-contain" />
          {isRevoked ? (
            <div className="bg-red-100 text-red-700 px-4 py-1.5 rounded-full text-sm font-bold flex items-center gap-2">
              <XCircle className="w-4 h-4" /> Certificate Revoked
            </div>
          ) : isLapsed ? (
            <div className="bg-red-100 text-red-700 px-4 py-1.5 rounded-full text-sm font-bold flex items-center gap-2">
              <XCircle className="w-4 h-4" /> Certificate Expired
            </div>
          ) : (
            <div className="bg-green-100 text-green-700 px-4 py-1.5 rounded-full text-sm font-bold flex items-center gap-2">
              <CheckCircle className="w-4 h-4" /> Certificate Verified
            </div>
          )}
        </div>

        {/* Validation Message */}
        <div className="p-6 text-center border-b border-slate-100 bg-slate-50/50">
          <p
            className={`text-sm font-medium ${isRevoked || isLapsed ? 'text-red-600' : 'text-slate-700'}`}
          >
            {isRevoked
              ? `This certificate has been revoked${revokedAt ? ` on ${formatCertificateDate(revokedAt)}` : ''} and is no longer valid.`
              : isLapsed
                ? 'This certificate is no longer valid as it has passed its expiry date.'
                : config.validMessage}
          </p>
        </div>

        {/* Details */}
        <div className="p-6 space-y-4">
          {config.showName && certificateName && (
            <div className="flex items-start gap-4">
              <div className="bg-blue-50 p-2 rounded-lg text-blue-600">
                <User className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <p className="text-xs font-bold text-slate-400 uppercase">Patient Name</p>
                <p className="font-bold text-slate-900 text-lg">{certificateName}</p>
              </div>
            </div>
          )}

          {config.showRef && resolvedCertId && (
            <div className="flex items-start gap-4">
              <div className="bg-purple-50 p-2 rounded-lg text-purple-600">
                <FileText className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <p className="text-xs font-bold text-slate-400 uppercase">Certificate ID</p>
                <p className="font-mono text-slate-700 text-xs break-all leading-tight">
                  {resolvedCertId}
                </p>
              </div>
            </div>
          )}

          {config.showDate && issuedAt && (
            <div className="flex items-start gap-4">
              <div className="bg-orange-50 p-2 rounded-lg text-orange-600">
                <Calendar className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <p className="text-xs font-bold text-slate-400 uppercase">Assessment Date</p>
                <p className="font-medium text-slate-800">{formatCertificateDate(issuedAt)}</p>
              </div>
            </div>
          )}

          {config.showExpiry && validUntil && (
            <div className="flex items-start gap-4">
              <div
                className={`p-2 rounded-lg ${isLapsed ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-600'}`}
              >
                <Clock className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <p className="text-xs font-bold text-slate-400 uppercase">Expiry Date</p>
                <p className={`font-bold ${isLapsed ? 'text-red-600' : 'text-slate-800'}`}>
                  {formatCertificateDate(validUntil)}
                </p>
              </div>
            </div>
          )}

          {config.showOutcome && certificateOutcome && (
            <div className="flex items-start gap-4">
              <div className="bg-teal-50 p-2 rounded-lg text-teal-600">
                <Activity className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <p className="text-xs font-bold text-slate-400 uppercase">Outcome</p>
                <p className="font-bold text-slate-900 capitalize">
                  {certificateOutcome.replace(/_/g, ' ')}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* URL Check Option */}
        {config.showUrl && (
          <div className="px-6 pb-6 pt-0">
            <div className="p-3 bg-slate-50 border border-slate-100 rounded-lg text-center">
              <p className="text-[10px] text-slate-400 uppercase font-bold mb-1">
                Verification URL
              </p>
              <div className="flex items-center justify-center gap-1 text-xs text-teal-600 font-medium break-all">
                <Link className="w-3 h-3 flex-shrink-0" />
                {displayUrl}
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="bg-slate-50 p-4 text-center border-t border-slate-100">
          <p className="text-xs text-slate-400">
            Verified by {systemConfig?.general.appName || 'K2 OccuHealth Pro'}
          </p>
          <p className="text-[10px] text-slate-300 mt-1">{formatUkDateTime(new Date(), '—')}</p>
        </div>
      </div>
    </div>
  );
};
